/**
 * DownloadProgressModal - Shows download progress with DRM feedback
 *
 * Features:
 * - Real-time progress updates
 * - Chapter-by-chapter progress tracking
 * - Shows rejected chapters (quota exceeded)
 * - Cancel download option
 * - Success/error states
 *
 * Mobile-first design with bottom sheet on mobile, modal on desktop
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';
import type { DownloadProgress } from '@/types/offline';

export interface DownloadProgressModalProps {
  progress: DownloadProgress | null;
  isOpen: boolean;
  onCancel: () => void;
  onClose: () => void;
}

export function DownloadProgressModal({
  progress,
  isOpen,
  onCancel,
  onClose,
}: DownloadProgressModalProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      dialogRef.current?.focus();
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus?.();
    }
  }, [isOpen]);

  if (!isOpen || !progress) return null;

  const isComplete = progress.status === 'complete';
  const isFailed = progress.status === 'failed';
  const isDownloading = progress.status === 'downloading';
  const percentComplete = Math.round(progress.percentComplete);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={isComplete || isFailed ? onClose : undefined}
            role="presentation"
            onKeyDown={(e) => {
              if (e.key === 'Escape' && (isComplete || isFailed)) {
                onClose();
              }
            }}
            className="fixed inset-0 bg-black/50 z-[100] backdrop-blur-sm"
          />

          {/* Modal Content */}
          <motion.div
            ref={dialogRef}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="download-progress-title"
            aria-describedby="download-progress-status"
            tabIndex={-1}
            className="fixed bottom-0 left-0 right-0 md:bottom-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-md md:rounded-2xl z-[101] bg-white dark:bg-neutral-900 shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center gap-3">
                {isDownloading && <Loader2 className="w-5 h-5 text-primary-600 dark:text-primary-400 animate-spin" />}
                {isComplete && <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />}
                {isFailed && <XCircle className="w-5 h-5 text-error-600 dark:text-error-400" />}

                <h2 id="download-progress-title" className="text-lg font-bold text-neutral-900 dark:text-white">
                  {isDownloading
                    ? t('App.Offline.DownloadProgressModal.TitleDownloading')
                    : isComplete
                    ? t('App.Offline.DownloadProgressModal.TitleComplete')
                    : t('App.Offline.DownloadProgressModal.TitleFailed')}
                </h2>
              </div>

              {(isComplete || isFailed) && (
                <button
                  onClick={onClose}
                  className="flex-shrink-0 p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                </button>
              )}
            </div>

            {/* Progress Content */}
            <div id="download-progress-status" role="status" aria-live="polite" aria-atomic="true" className="p-6">
              {/* Progress Bar */}
              {isDownloading && (
                <>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                        {t('App.Offline.DownloadProgressModal.Progress')}
                      </span>
                      <span className="text-sm font-bold text-neutral-900 dark:text-white">
                        {percentComplete}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-neutral-200 dark:bg-neutral-800 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentComplete}%` }}
                        transition={{ duration: 0.3 }}
                        className="h-full bg-primary-600 dark:bg-primary-500 rounded-full"
                      />
                    </div>
                  </div>

                  {/* Chapter Progress */}
                  <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                    <span>{t('App.Offline.DownloadProgressModal.ChaptersDownloaded')}</span>
                    <span className="font-medium">
                      {progress.downloadedChunks} / {progress.totalChunks}
                    </span>
                  </div>

                  {/* Download Speed/Size (if available) */}
                  {progress.downloadedBytes > 0 && (
                    <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400">
                      <span>{t('App.Offline.DownloadProgressModal.Downloaded')}</span>
                      <span className="font-medium">
                        {(progress.downloadedBytes / (1024 * 1024)).toFixed(2)} MB
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Success Message */}
              {isComplete && (
                <div className="text-center py-4">
                  <CheckCircle className="w-16 h-16 text-success-600 dark:text-success-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
                    {t('App.Offline.DownloadProgressModal.SuccessMessage')}
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    {t('App.Offline.DownloadProgressModal.SuccessChapters', { count: progress.downloadedChunks })}
                  </p>
                </div>
              )}

              {/* Error Message */}
              {isFailed && (
                <div className="text-center py-4">
                  <XCircle className="w-16 h-16 text-error-600 dark:text-error-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
                    {t('App.Offline.DownloadProgressModal.TitleFailed')}
                  </p>
                  {progress.error && (
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      {progress.error}
                    </p>
                  )}
                  <div className="mt-4 p-3 bg-warning-50 dark:bg-warning-900/20 rounded-lg border border-warning-200 dark:border-warning-800">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-warning-800 dark:text-warning-200">
                        {t('App.Offline.DownloadProgressModal.FailedRetryHint')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800">
              {isDownloading ? (
                <button
                  onClick={onCancel}
                  className="w-full px-4 py-3 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors"
                >
                  {t('App.Offline.DownloadProgressModal.CancelDownload')}
                </button>
              ) : (
                <button
                  onClick={onClose}
                  className="w-full px-4 py-3 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  {isComplete ? t('App.Offline.DownloadProgressModal.Close') : t('App.Offline.DownloadProgressModal.Dismiss')}
                </button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
